"use client";

/**
 * Port of the ajaraam ColorPicker - HSV canvas + hue slider + hex/rgb/hsl
 * inputs in a Popover. Identical UX so AiShop's brand-color picker matches
 * the rest of Aimar's tooling.
 */
import * as React from "react";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Slider } from "./slider";
import { cn } from "../lib/utils";

interface ColorPickerProps {
  value?:    string;
  onChange?: (color: string) => void;
  className?: string;
}

// ── Conversion helpers ──────────────────────────────────────────────────────

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function hexToHsv(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const diff = max - min;
  let h = 0, s = 0;
  const v = max;
  if (diff !== 0) {
    s = diff / max;
    switch (max) {
      case r: h = ((g - b) / diff + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / diff + 2) / 6; break;
      case b: h = ((r - g) / diff + 4) / 6; break;
    }
  }
  return [h * 360, s * 100, v * 100];
}

function hsvToHex(h: number, s: number, v: number): string {
  s /= 100; v /= 100;
  const i = Math.floor(h / 60) % 6;
  const f = h / 60 - Math.floor(h / 60);
  const p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s);
  let r = 0, g = 0, b = 0;
  switch (i) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function hexToHsl(hex: string): [number, number, number] {
  const [r0, g0, b0] = hexToRgb(hex);
  const r = r0 / 255, g = g0 / 255, b = b0 / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

type Format = "hex" | "rgb" | "hsl";

export function ColorPicker({ value = "#000000", onChange, className }: ColorPickerProps) {
  const [open, setOpen] = React.useState(false);

  const [hue, setHue] = React.useState(0);
  const [sat, setSat] = React.useState(100);
  const [val, setVal] = React.useState(100);
  const [hexInput, setHexInput] = React.useState(value);
  const [rgb, setRgb] = React.useState<[number, number, number]>([255, 0, 0]);
  const [hsl, setHsl] = React.useState<[number, number, number]>([0, 100, 50]);
  const [format, setFormat] = React.useState<Format>("hex");
  const [isDragging, setIsDragging] = React.useState(false);
  const canvasRef = React.useRef<HTMLDivElement>(null);

  const syncFromHex = React.useCallback((hex: string) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
    const [h, s, v] = hexToHsv(hex);
    setHue(h); setSat(s); setVal(v);
    setHexInput(hex);
    setRgb(hexToRgb(hex));
    setHsl(hexToHsl(hex));
  }, []);

  React.useEffect(() => { syncFromHex(value); }, [value, syncFromHex]);

  const emitColor = React.useCallback((h: number, s: number, v: number) => {
    const hex = hsvToHex(h, s, v);
    setHue(h); setSat(s); setVal(v);
    setHexInput(hex);
    setRgb(hexToRgb(hex));
    setHsl(hexToHsl(hex));
    onChange?.(hex);
  }, [onChange]);

  const handleCanvasPointer = React.useCallback((e: React.PointerEvent<HTMLDivElement> | PointerEvent) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
    emitColor(hue, x * 100, (1 - y) * 100);
  }, [hue, emitColor]);

  React.useEffect(() => {
    if (!isDragging) return;
    const move = (e: PointerEvent) => handleCanvasPointer(e);
    const up = () => setIsDragging(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [isDragging, handleCanvasPointer]);

  const hueColor = `hsl(${hue}, 100%, 50%)`;
  const thumbX = sat;
  const thumbY = 100 - val;
  const displayColor = hsvToHex(hue, sat, val);
  const formats: Format[] = ["hex", "rgb", "hsl"];

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-10 w-full rounded-md border border-input bg-card px-2 py-1 text-sm shadow-soft hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 inline-flex items-center gap-2",
            className,
          )}
        >
          <div className="size-5 rounded border border-input shrink-0" style={{ backgroundColor: displayColor }} />
          <span className="text-xs font-mono text-muted-foreground">{displayColor}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" sideOffset={6} className="w-72 p-4 space-y-3 z-[200]">
        {/* Saturation / Value canvas */}
        <div
          ref={canvasRef}
          className="relative w-full rounded-md cursor-crosshair select-none"
          style={{
            height: 160,
            background: [
              "linear-gradient(to top, #000, transparent)",
              `linear-gradient(to right, #fff, ${hueColor})`,
            ].join(", "),
          }}
          onPointerDown={(e) => { setIsDragging(true); handleCanvasPointer(e); }}
        >
          <div
            className="absolute size-3.5 rounded-full border-2 border-white shadow-md pointer-events-none"
            style={{
              left: `${thumbX}%`,
              top:  `${thumbY}%`,
              transform: "translate(-50%, -50%)",
              backgroundColor: displayColor,
              boxShadow: "0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)",
            }}
          />
        </div>

        {/* Hue slider */}
        <div className="relative h-4">
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
            }}
          />
          <div className="relative [&_[role=slider]]:border-2 [&_[role=slider]]:border-white [&_[role=slider]]:shadow-md [&_[role=slider]]:bg-transparent">
            <Slider
              value={[hue]}
              min={0}
              max={360}
              step={1}
              onValueChange={([h]) => emitColor(h!, sat, val)}
              className="[&_[data-slot=slider-track]]:bg-transparent [&_[data-slot=slider-range]]:bg-transparent"
            />
          </div>
        </div>

        {/* Format tabs + preview */}
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-md border border-input shrink-0" style={{ backgroundColor: displayColor }} />
          <div className="flex gap-1 flex-1">
            {formats.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={cn(
                  "flex-1 rounded text-[10px] font-medium py-1 uppercase transition-colors",
                  format === f ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80",
                )}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Format inputs */}
        {format === "hex" && (
          <Input
            value={hexInput}
            onChange={(e) => {
              const v = e.target.value;
              setHexInput(v);
              if (/^#[0-9a-fA-F]{6}$/.test(v)) { syncFromHex(v); onChange?.(v); }
            }}
            onBlur={(e) => {
              let v = e.target.value.trim();
              if (!v.startsWith("#")) v = "#" + v;
              const hex = "#" + v.slice(1).replace(/[^0-9a-fA-F]/g, "").slice(0, 6).padEnd(6, "0");
              syncFromHex(hex); onChange?.(hex);
            }}
            className="h-8 text-xs font-mono"
            placeholder="#000000"
          />
        )}
        {format === "rgb" && (
          <div className="grid grid-cols-3 gap-1.5">
            {(["R", "G", "B"] as const).map((ch, i) => (
              <div key={ch} className="space-y-0.5">
                <p className="text-[10px] text-center text-muted-foreground font-medium">{ch}</p>
                <Input
                  type="number"
                  min={0} max={255}
                  value={rgb[i]}
                  onChange={(e) => {
                    const newRgb: [number, number, number] = [...rgb];
                    newRgb[i] = clamp(parseInt(e.target.value) || 0, 0, 255);
                    const hex = rgbToHex(...newRgb);
                    syncFromHex(hex); onChange?.(hex);
                  }}
                  className="h-8 text-xs text-center px-1"
                />
              </div>
            ))}
          </div>
        )}
        {format === "hsl" && (
          <div className="grid grid-cols-3 gap-1.5">
            {([["H", 0, 360], ["S", 0, 100], ["L", 0, 100]] as [string, number, number][]).map(([ch, min, max], i) => (
              <div key={ch} className="space-y-0.5">
                <p className="text-[10px] text-center text-muted-foreground font-medium">{ch}</p>
                <Input
                  type="number"
                  min={min} max={max}
                  value={hsl[i]}
                  onChange={(e) => {
                    const newHsl: [number, number, number] = [...hsl];
                    newHsl[i] = clamp(parseInt(e.target.value) || 0, min, max);
                    const hex = hslToHex(...newHsl);
                    syncFromHex(hex); onChange?.(hex);
                  }}
                  className="h-8 text-xs text-center px-1"
                />
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
